import * as React from "react";
import { MDXProvider } from "@mdx-js/react";
import { graphql } from "gatsby";
import { MDXRenderer } from "gatsby-plugin-mdx";
import { Link as GatsbyLink } from 'gatsby-plugin-material-ui';

import { StyledEngineProvider } from '@material-ui/core/styles';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import Box from '@material-ui/core/Box';
import Chip from '@material-ui/core/Chip';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import Stack from '@material-ui/core/Stack';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableFooter from '@material-ui/core/TableFooter';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from "@material-ui/core/Typography";

import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import Brewlog, { TagList } from './Brewlog';
import BlogChip from './BlogChip';
import BlogNavigation from './BlogNavigation';
import Layout from './Layout';
import Link from './Link';

function AccordionPanel({ id, title, children }) {
  return (
    <Accordion id={id}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`${id}-content`}
        id={`${id}-header`}
      >
        <Typography component="h3" variant="h5">{title}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        {children}
      </AccordionDetails>
    </Accordion>
  );
}

const brewSummaryColumns = [
  {
    label: "Brewers",
    key: "brewers",
    format: value => value
  },
  {
    label: "Volume",
    key: "volume",
    format: value => `${value || '?'} gallons`
  },
  {
    label: "Brew date",
    key: "brew_date",
    format: value => value
  },
  {
    label: "Bottling date",
    key: "bottling_date",
    format: value => value || '?'
  },
  {
    label: "Original gravity",
    key: "og",
    format: value => value || '?'
  },
  {
    label: "Final gravity",
    key: "fg",
    format: value => value || '?'
  },
  {
    label: "Alcohol by volume",
    key: "abv",
    format: value => `${value || '?'}%`
  },
  {
    label: "Estimated bitterness",
    key: "bitterness",
    format: value => `${value || '?'} IBU`
  },
  {
    label: "Estimated calories",
    key: "calories",
    format: value => `${value || '?'} Calories/12 oz`
  },
  {
    label: "Estimated carbohydrates",
    key: "carbs",
    format: value => `${value || '?'} g/12 oz`
  },
];

const mdxComponents = {
  h1: props => <Typography component="h4" variant="h6" {...props} />,
  p: props => <Typography paragraph {...props} />,
  a: Link
}

export default function BrewlogArticle({ doc, tags, articles }) {
  const { frontmatter } = doc;
  return (
    <StyledEngineProvider injectFirst>
      <MDXProvider components={mdxComponents}>
        <Layout subtitle="Brewlog">
          <Brewlog>
            <Container>
              <Typography component="h2" variant="h4">
                {frontmatter.title}
                {frontmatter.subtitle &&
                  <Typography component="span" variant="subtitle1" marginLeft={1}>
                    {frontmatter.subtitle}
                  </Typography>
                }
              </Typography>
              <MDXRenderer>{frontmatter.description}</MDXRenderer>

              <BlogNavigation doc={doc} tags={tags} articles={articles} />

              <Box mt={2}>
                <MDXRenderer>{frontmatter.brew.long_description}</MDXRenderer>
              </Box>
            </Container>

            <Grid container p={2} spacing={2}>
              <Grid item xs={12}>
                <BrewSummary info={frontmatter.brew.info} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Ingredients ingredients={frontmatter.brew.ingredients} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Recipe recipe={frontmatter.brew.recipe} />
              </Grid>
              <Grid item xs={12} md={8} mt={2}>
                <Workbook workbook={frontmatter.brew.workbook} />
              </Grid>
            </Grid>

            <Container>
              <Typography component="h3" variant="h5" mt={4} gutterBottom>Brew log</Typography>
              <MDXRenderer>{doc.body}</MDXRenderer>
            </Container>

            <Container>
              <TagList tags={frontmatter.tags.map(tag => ({ name: tag }))}>
                <BlogChip label={frontmatter.date} color="secondary" />
              </TagList>
            </Container>
          </Brewlog>
        </Layout>
      </MDXProvider>
    </StyledEngineProvider >
  );
}


function BrewSummary({ info }) {
  return (
    <Box sx={{ width: '100%', maxWidth: 600 }}>
      <Typography component="h3" variant="h5" mt={2} gutterBottom>
        Brew summary
      </Typography>
      <Grid
        container
        component='dl'
        spacing={1}>
        {brewSummaryColumns.map(item =>
          [
            <Grid item xs={4} key={item.label} component="dt"
              sx={{ fontStyle: "italic", textAlign: "right" }}>
              {item.label}
            </Grid>,
            <Grid item xs={8} key={`${item.label}-content`} component="dd">
              {item.format(info[item.key])}
            </Grid>
          ]
        )}
      </Grid>
    </Box>
  );
}

function BrewTable(props) {
  const { id, headings, rows, footer } = props;
  const size = props.size || "small";

  return (
    <TableContainer>
      <Table size={size}>
        <TableHead>
          <TableRow>
            {headings.map(head =>
              <TableCell key={head}>
                {head}
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, row_index) =>
            <TableRow key={`${id}-${row_index}`}>
              {row.map((cell, cell_index) =>
                <TableCell key={`${id}-${row_index}-${cell_index}`}>{cell}</TableCell>
              )}
            </TableRow>
          )}
        </TableBody>
        {footer &&
          <TableFooter>
            {footer.map((row, row_index) =>
              <TableRow key={`${id}-footer-${row_index}`}>
                <TableCell colSpan={headings.length}>{row}</TableCell>
              </TableRow>
            )}
          </TableFooter>
        }
      </Table>
    </TableContainer>
  )
}

function Ingredients({ ingredients }) {
  return (ingredients &&
    <>
      <Typography component="h3" variant="h5" gutterBottom>Ingredients</Typography>
      <BrewTable
        id='ingredients'
        headings={['Quantity', 'Item', 'Timing']}
        rows={ingredients}
      />
    </>
  );
}

function Recipe({ recipe }) {
  return (
    recipe &&
    <>
      <Typography component="h3" variant="h5" gutterBottom>Recipe</Typography>
      <MDXRenderer>{recipe}</MDXRenderer>
    </>
  );
}

function Workbook({ workbook }) {
  return (
    workbook &&
    <Box>
      <AccordionPanel id="mash-panel" title="Mash">
        <BrewTable
          id="mash"
          {...workbook.mash}
        />
      </AccordionPanel>
      <AccordionPanel id="latuer-and-sparge-panel" title="Lauter and sparge">
        <BrewTable
          id="lauter-and-sparge-table"
          {...workbook.lauter_and_sparge}
        />
      </AccordionPanel>
      <AccordionPanel id="hopping-panel" title="Hopping">
        <BrewTable
          id="hopping-table"
          {...workbook.hopping}
        />
      </AccordionPanel>
      <AccordionPanel id="fermentation-panel" title="Fermentation">
        <BrewTable
          id="fermentation-table"
          {...workbook.fermentation}
        />
      </AccordionPanel>
    </Box>
  );
}

export const brewlogData = graphql`
fragment brewlogData on Mdx {
  id
  body
  frontmatter {
    author
    brew {
      info {
        abv
        brew_date(formatString: "YYYY MMMM DD")
        bottling_date(formatString: "YYYY MMMM DD")
        brewers
        calories
        carbs
        fg
        number
        og
        volume
      }
      long_description
      ingredients
      recipe
      workbook {
        mash {
          footer
          headings
          rows
        }
        lauter_and_sparge {
          footer
          headings
          rows
        }
        hopping {
          footer
          headings
          rows
        }
        fermentation {
          headings
          rows
        }
      }
    }
    date(formatString: "YYYY MMMM DD")
    description
    subtitle
    tags
    title
  }
}`;